import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@kizen/kds/Icon';
import IconButton from '__components/Kizen/IconButton';
import { colorsButton } from 'app/colors';
import Checkbox from '__components/Checkbox/Checkbox';
import BasicTable from '__components/Tables/Basic';
import { SizedCell, SortableHeadCell, TRow } from '__components/Kizen/Table';
import { Typography } from '@kizen/kds/Typography';

const CELL_PADDING = '15px';

const itemRowClassName = (indent: boolean, active: boolean) =>
  [
    'flex items-center',
    indent ? 'pl-[45px] pr-[15px]' : 'px-[15px]',
    active ? 'bg-[var(--color-background-hover)]' : 'bg-transparent',
  ].join(' ');

const cellContentClassName = (
  align: 'start' | 'center' | undefined,
  active: boolean
) =>
  [
    'flex items-center px-[15px]',
    align === 'center' ? 'justify-center' : 'justify-start',
    active ? 'bg-[var(--color-background-hover)]' : 'bg-transparent',
  ].join(' ');

// grid-template-rows 0fr/1fr, since a real <tr> height can't be transitioned.
const collapseClassName = (expanded: boolean) =>
  [
    'grid -mx-[15px] transition-[grid-template-rows] duration-200',
    '[transition-timing-function:ease]',
    expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
  ].join(' ');

const rotatingChevronClassName = (expanded: boolean) =>
  [
    'ml-2 transition-transform duration-150 [transition-timing-function:ease]',
    expanded ? 'rotate-0' : '-rotate-90',
  ].join(' ');

export type CollapsibleGroupedTableColumn<TItem> = {
  id: string;
  label: ReactNode;
  width?: string;
  align?: 'start' | 'center';
  renderItem: (item: TItem) => ReactNode;
};

export type CollapsibleGroupedTableGroup<TItem> = {
  key: string;
  label: ReactNode;
  items: TItem[];
};

export type CollapsibleGroupedTableProps<TItem> = {
  groups: CollapsibleGroupedTableGroup<TItem>[];
  columns: CollapsibleGroupedTableColumn<TItem>[];
  nameColumnLabel: ReactNode;
  nameColumnWidth?: string;
  getItemId: (item: TItem) => string;
  renderItemLabel: (item: TItem) => ReactNode;
  selectedIds: Set<string>;
  onChangeSelectedIds: (next: Set<string>) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  itemHeight?: string;
  renderGroupLabel?: (
    group: CollapsibleGroupedTableGroup<TItem>,
    selectedCount: number
  ) => ReactNode;
};

type RowItem<TItem> =
  | { kind: 'group'; key: string; group: CollapsibleGroupedTableGroup<TItem> }
  | {
      kind: 'group-body';
      key: string;
      group: CollapsibleGroupedTableGroup<TItem>;
    };

export const CollapsibleGroupedTable = <TItem,>({
  groups,
  columns,
  nameColumnLabel,
  nameColumnWidth = '340px',
  getItemId,
  renderItemLabel,
  selectedIds,
  onChangeSelectedIds,
  disabled = false,
  defaultExpanded = true,
  itemHeight = '32px',
  renderGroupLabel,
}: CollapsibleGroupedTableProps<TItem>) => {
  const { t } = useTranslation();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const prevGroupKeys = useRef<string>('');
  const groupKeysSignature = groups.map((group) => group.key).join(',');
  if (groups.length && prevGroupKeys.current !== groupKeysSignature) {
    prevGroupKeys.current = groupKeysSignature;
    if (defaultExpanded) {
      setExpandedGroups(new Set(groups.map((group) => group.key)));
    }
  }

  const toggleGroupExpanded = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const toggleItem = useCallback(
    (itemId: string) => {
      const next = new Set(selectedIds);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      onChangeSelectedIds(next);
    },
    [selectedIds, onChangeSelectedIds]
  );

  const toggleGroupItems = useCallback(
    (group: CollapsibleGroupedTableGroup<TItem>, allSelected: boolean) => {
      const next = new Set(selectedIds);
      group.items.forEach((item) => {
        const id = getItemId(item);
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });
      onChangeSelectedIds(next);
    },
    [selectedIds, onChangeSelectedIds, getItemId]
  );

  const handleItemMouseEnter = useCallback(
    (id: string) => setHoveredId(id),
    []
  );
  const handleItemMouseLeave = useCallback(
    (id: string) => setHoveredId((prev) => (prev === id ? null : prev)),
    []
  );

  const rows = useMemo<RowItem<TItem>[]>(
    () =>
      groups.flatMap((group) => [
        { kind: 'group', key: group.key, group } as RowItem<TItem>,
        {
          kind: 'group-body',
          key: `${group.key}-body`,
          group,
        } as RowItem<TItem>,
      ]),
    [groups]
  );

  const tableColumns = useMemo(() => {
    const nameColumn = {
      id: 'name',
      headCell: (
        <SortableHeadCell
          hasSorting={false}
          label={nameColumnLabel}
          width={nameColumnWidth}
          padding={CELL_PADDING}
        />
      ),
      cell: <SizedCell width={nameColumnWidth} padding={CELL_PADDING} />,
      format: (_: unknown, row: RowItem<TItem>) => {
        if (row.kind === 'group') {
          const selectedCount = row.group.items.filter((item) =>
            selectedIds.has(getItemId(item))
          ).length;
          const allSelected =
            row.group.items.length > 0 &&
            selectedCount === row.group.items.length;
          const someSelected = selectedCount > 0 && !allSelected;
          const expanded = expandedGroups.has(row.group.key);

          return (
            <div className="-mx-[15px]">
              <div
                className={itemRowClassName(false, hoveredId === row.group.key)}
                style={{ height: itemHeight }}
                onMouseEnter={() => handleItemMouseEnter(row.group.key)}
                onMouseLeave={() => handleItemMouseLeave(row.group.key)}
              >
                <Checkbox
                  className="ml-0 mr-[4px]"
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={disabled}
                  onChange={() => toggleGroupItems(row.group, allSelected)}
                  data-qa-id={`collapsible-group-checkbox-${row.group.key}`}
                />
                <Typography
                  variant="body"
                  weight="semibold"
                  className="-ml-[8px] mr-[6px]"
                >
                  {renderGroupLabel
                    ? renderGroupLabel(row.group, selectedCount)
                    : t('{{label}} ({{selected}} / {{total}})', {
                        label: row.group.label,
                        selected: selectedCount,
                        total: row.group.items.length,
                      })}
                </Typography>
                <IconButton
                  sizing="dense"
                  color={colorsButton.iconGray}
                  className={rotatingChevronClassName(expanded)}
                  onClick={() => toggleGroupExpanded(row.group.key)}
                  title={expanded ? t('Collapse') : t('Expand')}
                  data-qa-id={`collapsible-group-toggle-${row.group.key}`}
                >
                  <Icon icon="custom-chevron-down" size="sm" />
                </IconButton>
              </div>
            </div>
          );
        }

        const expanded = expandedGroups.has(row.group.key);

        return (
          <div className={collapseClassName(expanded)}>
            <div className="overflow-hidden min-h-0">
              {row.group.items.map((item) => {
                const id = getItemId(item);
                return (
                  <div
                    key={id}
                    className={itemRowClassName(true, hoveredId === id)}
                    style={{ height: itemHeight }}
                    onMouseEnter={() => handleItemMouseEnter(id)}
                    onMouseLeave={() => handleItemMouseLeave(id)}
                  >
                    <Checkbox
                      className="ml-0 mr-[4px]"
                      checked={selectedIds.has(id)}
                      disabled={disabled}
                      onChange={() => toggleItem(id)}
                      data-qa-id={`collapsible-item-checkbox-${id}`}
                    />
                    <button
                      type="button"
                      className={`min-w-0 border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:outline focus-visible:outline-1 ${
                        disabled ? 'cursor-default' : 'cursor-pointer'
                      }`}
                      disabled={disabled}
                      onClick={() => toggleItem(id)}
                      data-qa-id={`collapsible-item-label-${id}`}
                    >
                      {renderItemLabel(item)}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      },
    };

    const restColumns = columns.map((column) => ({
      id: column.id,
      headCell: (
        <SortableHeadCell
          hasSorting={false}
          label={column.label}
          width={column.width}
          padding={CELL_PADDING}
        />
      ),
      cell: <SizedCell width={column.width} padding={CELL_PADDING} />,
      format: (_: unknown, row: RowItem<TItem>) => {
        if (row.kind === 'group') {
          return (
            <div
              className={`-mx-[15px] ${cellContentClassName(
                column.align,
                hoveredId === row.group.key
              )}`}
              style={{ height: itemHeight }}
              onMouseEnter={() => handleItemMouseEnter(row.group.key)}
              onMouseLeave={() => handleItemMouseLeave(row.group.key)}
            />
          );
        }

        return (
          <div className={collapseClassName(expandedGroups.has(row.group.key))}>
            <div className="overflow-hidden min-h-0">
              {row.group.items.map((item) => {
                const id = getItemId(item);
                return (
                  <div
                    key={id}
                    className={cellContentClassName(
                      column.align,
                      hoveredId === id
                    )}
                    style={{ height: itemHeight }}
                    onMouseEnter={() => handleItemMouseEnter(id)}
                    onMouseLeave={() => handleItemMouseLeave(id)}
                  >
                    {column.renderItem(item)}
                  </div>
                );
              })}
            </div>
          </div>
        );
      },
    }));

    return [nameColumn, ...restColumns];
  }, [
    columns,
    nameColumnLabel,
    nameColumnWidth,
    selectedIds,
    expandedGroups,
    hoveredId,
    disabled,
    itemHeight,
    getItemId,
    renderItemLabel,
    renderGroupLabel,
    t,
    toggleGroupItems,
    toggleGroupExpanded,
    toggleItem,
    handleItemMouseEnter,
    handleItemMouseLeave,
  ]);

  return (
    // Body rows hold a whole group's items; the table's default 40px row height would
    // otherwise leave a blank band under every collapsed group.
    <div className="[&_tr.kzn-collapsible-row:hover]:!bg-transparent [&_thead>tr]:!h-[40px] [&_tr.kzn-collapsible-body]:!h-0 [&_tr.kzn-collapsible-body>td]:!h-0">
      <BasicTable head={<TRow head columns={tableColumns} data={{}} />}>
        {rows.map((row) => (
          <TRow
            key={row.key}
            columns={tableColumns}
            data={row}
            className={
              row.kind === 'group-body'
                ? 'kzn-collapsible-row kzn-collapsible-body'
                : 'kzn-collapsible-row'
            }
          />
        ))}
      </BasicTable>
    </div>
  );
};
